import { Fragment, useEffect, useState, type ReactNode } from "react";
import {
  Check,
  ChevronDown,
  CircleDashed,
  Eye,
  EyeOff,
  Loader2,
  ShieldAlert,
  ShieldQuestion,
  X,
} from "lucide-react";
import { useTranslation, type TranslateFn } from "@/shared/i18n/LocaleProvider";
import type { RunErrorDetail, ToolActivity } from "@/features/intelligence/lib/thread-state";
import {
  elapsedLabel,
  outcomeKey,
  wasOutcomeReadBack,
  toolOutcome,
  OUTCOME_AWAITING,
  OUTCOME_PENDING,
  type ToolOutcome,
} from "@/features/intelligence/lib/tool-outcome";
import {
  frontendOutcome,
  frontendOutcomeKey,
  type FrontendCallRecord,
  type FrontendOutcome,
} from "@/features/intelligence/lib/frontend-tool-outcome";
import { cn } from "@/shared/cn";

export interface ToolCallStripProps {
  tools: ToolActivity[];
  runError?: RunErrorDetail | null;
  awaitingApprovalFor?: string | null;
  frontendCalls?: ReadonlyMap<string, FrontendCallRecord>;
  startedAt?: Readonly<Record<string, number>>;
  readBack?: readonly string[];
  below?: (tool: ToolActivity) => ReactNode;
  stage?: (tool: ToolActivity) => ToolStage | null;
}

export type ToolStage = "awaiting" | "done";

export function ToolCallStrip({
  tools,
  runError = null,
  awaitingApprovalFor = null,
  frontendCalls,
  startedAt,
  readBack,
  below,
  stage,
}: ToolCallStripProps) {
  if (tools.length === 0) return null;
  const cards = new Map(tools.map((tool) => [tool.toolCallId, below?.(tool) ?? null]));
  const runs = toolRuns(
    tools,
    (tool) =>
      frontendCalls?.has(tool.toolCallId) === true ||
      (stage?.(tool) ?? null) !== null ||
      cards.get(tool.toolCallId) != null,
  );
  const row = (tool: ToolActivity) => (
    <ToolCallRow
      key={tool.toolCallId}
      tool={tool}
      runError={runError}
      awaiting={tool.toolCallId === awaitingApprovalFor}
      frontend={frontendCalls?.get(tool.toolCallId)}
      startedAt={startedAt?.[tool.toolCallId] ?? null}
      readBack={readBack}
      stage={stage?.(tool) ?? null}
    />
  );
  return (
    <div className="flex w-full flex-col gap-[6px]">
      {runs.map((run) =>
        run.length > 1 ? (
          <ToolCallGroup
            key={run[0].toolCallId}
            tools={run}
            awaitingApprovalFor={awaitingApprovalFor}
            row={row}
          />
        ) : (
          <Fragment key={run[0].toolCallId}>
            {row(run[0])}
            {cards.get(run[0].toolCallId)}
          </Fragment>
        ),
      )}
    </div>
  );
}

function toolRuns(
  tools: readonly ToolActivity[],
  alone: (tool: ToolActivity) => boolean,
): ToolActivity[][] {
  const runs: ToolActivity[][] = [];
  for (const tool of tools) {
    const last = runs.at(-1);
    const joins =
      last !== undefined &&
      tool.toolName !== "" &&
      last[0].toolName === tool.toolName &&
      !alone(last[0]) &&
      !alone(tool);
    if (joins) last.push(tool);
    else runs.push([tool]);
  }
  return runs;
}

function ToolCallGroup({
  tools,
  awaitingApprovalFor,
  row,
}: {
  tools: ToolActivity[];
  awaitingApprovalFor: string | null;
  row: (tool: ToolActivity) => ReactNode;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const outcome = runOutcome(
    tools.map((tool) => toolOutcome(tool, tool.toolCallId === awaitingApprovalFor)),
  );
  return (
    <div
      data-tool-group={tools[0].toolName}
      className={cn(
        "w-full rounded-[var(--radius-md)] border border-[var(--color-light-02)]",
        "bg-[var(--color-paper)] text-sm",
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className={cn(
          "flex w-full items-center justify-between gap-[12px] px-[12px] py-[10px] text-left",
          "cursor-pointer rounded-[var(--radius-md)] transition-colors hover:bg-[var(--color-hover-faint)]",
        )}
      >
        <div className="flex min-w-0 items-center gap-[8px]">
          <OutcomeIcon outcome={outcome} />
          <span
            className="truncate text-[var(--color-text)]"
            style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.01em" }}
          >
            {toolLabel(t, tools[0].toolName)} ·{" "}
            <span className="text-[var(--color-text-muted)]">
              {t("intelligence.toolStrip.calls", { count: tools.length })}
            </span>
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-[8px]">
          <OutcomeBadge outcome={outcome} />
          <ChevronDown
            size={14}
            strokeWidth={2}
            className={cn(
              "text-[var(--color-text-muted)] transition-transform duration-150",
              expanded && "rotate-180",
            )}
          />
        </div>
      </button>
      {expanded && (
        <div className="flex flex-col gap-[6px] border-t border-[var(--color-light-02)] p-[8px]">
          {tools.map(row)}
        </div>
      )}
    </div>
  );
}

function runOutcome(outcomes: readonly ToolOutcome[]): ToolOutcome {
  for (const key of RUN_OUTCOME_ORDER) {
    const found = outcomes.find((outcome) => outcomeKey(outcome) === key);
    if (found !== undefined) return found;
  }
  return outcomes.find((outcome) => outcomeKey(outcome) !== "word:success") ?? outcomes[0];
}

function ToolCallRow({
  tool,
  runError,
  awaiting,
  frontend,
  startedAt,
  readBack,
  stage,
}: {
  tool: ToolActivity;
  runError: RunErrorDetail | null;
  awaiting: boolean;
  frontend: FrontendCallRecord | undefined;
  startedAt: number | null;
  readBack: readonly string[] | undefined;
  stage: ToolStage | null;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const frontendResult = frontendOutcome(frontend);
  const actualOutcome = toolOutcome(tool, awaiting);
  const delayedOutcome = useMinPendingDuration(actualOutcome);
  const outcome = actualOutcome.kind === "awaiting" ? actualOutcome : delayedOutcome;
  const elapsed = useElapsedLabel(outcome, startedAt);
  const fetched = wasOutcomeReadBack(outcome, tool.toolCallId, readBack);
  const staged = stage === null ? null : STAGE_OUTCOME[stage];

  const label = toolLabel(t, tool.toolName);
  const unknown = frontendResult === null && outcome.kind === "unknown";
  const expandable =
    unknown ||
    frontendResult !== null ||
    outcome.kind === "awaiting" ||
    tool.argsText !== "" ||
    tool.toolName !== "";

  return (
    <div
      className={cn(
        "w-full rounded-[var(--radius-md)] border border-[var(--color-light-02)]",
        "bg-[var(--color-paper)] text-sm",
      )}
    >
      <button
        type="button"
        onClick={expandable ? () => setExpanded((v) => !v) : undefined}
        disabled={!expandable}
        aria-expanded={expandable ? expanded : undefined}
        className={cn(
          "flex w-full items-center justify-between gap-[12px] px-[12px] py-[10px] text-left",
          expandable &&
            "cursor-pointer rounded-[var(--radius-md)] transition-colors hover:bg-[var(--color-hover-faint)]",
        )}
      >
        <div className="flex min-w-0 items-center gap-[8px]">
          {staged !== null ? (
            <OutcomeIcon outcome={staged} />
          ) : frontendResult ? (
            <FrontendIcon outcome={frontendResult} />
          ) : (
            <OutcomeIcon outcome={outcome} />
          )}
          <span
            className="truncate text-[var(--color-text)]"
            style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.01em" }}
          >
            {label}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-[8px]">
          {staged !== null ? (
            <OutcomeBadge outcome={staged} />
          ) : frontendResult ? (
            <>
              <span className="hidden text-xs uppercase tracking-[0.05em] text-[var(--color-text-muted)] sm:inline">
                {t("intelligence.toolStrip.frontend.kind")}
              </span>
              <FrontendBadge outcome={frontendResult} />
            </>
          ) : (
            <>
              {fetched && (
                <span
                  data-read-back=""
                  className="text-xs uppercase tracking-[0.05em] text-[var(--color-text-muted)]"
                >
                  {t("intelligence.toolStrip.outcome.recovered")}
                </span>
              )}
              {elapsed !== null && (
                <span
                  data-elapsed={elapsed}
                  className="tabular-nums text-xs text-[var(--color-text-muted)]"
                  style={{ fontFamily: "var(--font-mono)" }}
                >
                  {elapsed}
                </span>
              )}
              <OutcomeBadge outcome={outcome} />
            </>
          )}
          {expandable && (
            <ChevronDown
              size={14}
              strokeWidth={2}
              className={cn(
                "text-[var(--color-text-muted)] transition-transform duration-150",
                expanded && "rotate-180",
              )}
            />
          )}
        </div>
      </button>

      {expanded && expandable && (
        <div className="border-t border-[var(--color-light-02)] px-[12px] pb-[12px] pt-[10px]">
          {unknown && (
            <p
              className="mt-[8px] text-sm leading-relaxed text-[var(--color-text-muted)] first:mt-0"
              style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.01em" }}
            >
              {t("intelligence.toolStrip.unknownHint")}
            </p>
          )}
          {frontendResult && frontend?.message && (
            <p
              className="mt-[8px] text-sm leading-relaxed text-[var(--color-text-muted)] first:mt-0"
              style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.01em" }}
            >
              {frontend.message}
            </p>
          )}
          {outcome.kind === "awaiting" && (
            <p
              className="mt-[8px] text-sm leading-relaxed text-[var(--color-text-muted)] first:mt-0"
              style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.01em" }}
            >
              {t("intelligence.toolStrip.awaitingHint")}
            </p>
          )}
          {tool.toolName !== "" && (
            <div className="mt-[8px] first:mt-0">
              <p className="mb-[4px] text-xs font-medium uppercase tracking-[0.05em] text-[var(--color-text-muted)]">
                {t("intelligence.toolStrip.toolNameLabel")}
              </p>
              <code className="font-mono text-xs text-[var(--color-text-dim)]">
                {tool.toolName}
              </code>
            </div>
          )}
          {tool.argsText !== "" && (
            <div className="mt-[8px] first:mt-0">
              <p className="mb-[4px] text-xs font-medium uppercase tracking-[0.05em] text-[var(--color-text-muted)]">
                {t("intelligence.toolStrip.argsLabel")}
              </p>
              <pre className="max-h-64 overflow-auto rounded-[var(--radius-md)] border border-[var(--color-light-02)] bg-[var(--color-hover-faint)] px-[8px] py-[6px] font-mono text-xs leading-relaxed text-[var(--color-text-dim)]">
                {tool.argsText}
              </pre>
            </div>
          )}
          {unknown && runError && (
            <div className="mt-[8px] first:mt-0">
              <p className="mb-[4px] text-xs font-medium uppercase tracking-[0.05em] text-[var(--color-text-muted)]">
                {t("intelligence.toolStrip.endingLabel")}
              </p>
              <pre className="select-all overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-light-02)] bg-[var(--color-hover-faint)] px-[8px] py-[6px] font-mono text-xs leading-relaxed text-[var(--color-text-dim)]">
                {runErrorLines(runError)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function runErrorLines(detail: RunErrorDetail): string {
  return [
    detail.code !== null ? `code: ${detail.code}` : null,
    detail.correlationId !== null ? `correlationId: ${detail.correlationId}` : null,
    detail.message !== null ? `message: ${detail.message}` : null,
  ]
    .filter((line): line is string => line !== null)
    .join("\n");
}

function useElapsedLabel(outcome: ToolOutcome, startedAt: number | null): string | null {
  const pending = outcome.kind === "pending" && startedAt !== null;
  const [label, setLabel] = useState(() => elapsedLabel(outcome, startedAt, Date.now()));

  useEffect(() => {
    if (!pending) {
      setLabel(null);
      return;
    }
    setLabel(elapsedLabel(OUTCOME_PENDING, startedAt, Date.now()));
    const timer = window.setInterval(() => {
      setLabel(elapsedLabel(OUTCOME_PENDING, startedAt, Date.now()));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [pending, startedAt]);

  return pending ? label : null;
}

function useMinPendingDuration(outcome: ToolOutcome): ToolOutcome {
  const [mountedAt] = useState(() => performance.now());
  const [startedPending] = useState(() => outcome.kind === "pending");
  const [released, setReleased] = useState(false);
  const isPending = outcome.kind === "pending";

  useEffect(() => {
    if (released || !startedPending || isPending) return;
    const remaining = Math.max(0, MIN_PENDING_MS - (performance.now() - mountedAt));
    const timer = window.setTimeout(() => setReleased(true), remaining);
    return () => window.clearTimeout(timer);
  }, [isPending, released, startedPending, mountedAt]);

  if (!startedPending) return outcome;
  return released ? outcome : OUTCOME_PENDING;
}

function FrontendIcon({ outcome }: { outcome: FrontendOutcome }) {
  switch (outcome.kind) {
    case "running":
      return (
        <Loader2
          size={14}
          strokeWidth={2}
          className="shrink-0 animate-spin text-[var(--color-text-muted)]"
          aria-hidden
        />
      );
    case "shown":
      return <Eye size={14} strokeWidth={2} className="shrink-0 text-emerald-600" aria-hidden />;
    case "unconfirmed":
      return (
        <EyeOff
          size={14}
          strokeWidth={2}
          className="shrink-0 text-[var(--color-text-muted)]"
          aria-hidden
        />
      );
    case "refused":
      return <EyeOff size={14} strokeWidth={2} className="shrink-0 text-amber-600" aria-hidden />;
  }
}

function FrontendBadge({ outcome }: { outcome: FrontendOutcome }) {
  const { t } = useTranslation();
  const NEUTRAL = "bg-[var(--color-hover-faint)] text-[var(--color-text-muted)]";

  const { label, classes } = (() => {
    switch (outcome.kind) {
      case "running":
        return { label: t("intelligence.toolStrip.frontend.running"), classes: NEUTRAL };
      case "shown":
        return {
          label: t("intelligence.toolStrip.frontend.shown"),
          classes: "bg-emerald-50 text-emerald-700",
        };
      case "unconfirmed":
        return { label: t("intelligence.toolStrip.frontend.unconfirmed"), classes: NEUTRAL };
      case "refused":
        return {
          label: t("intelligence.toolStrip.frontend.refused"),
          classes: "bg-amber-50 text-amber-700",
        };
    }
  })();

  return (
    <span
      className={cn(
        "rounded-full px-[8px] py-[2px] text-xs font-medium uppercase tracking-[0.04em]",
        classes,
      )}
      data-frontend-outcome={frontendOutcomeKey(outcome)}
    >
      {label}
    </span>
  );
}

function OutcomeIcon({ outcome }: { outcome: ToolOutcome }) {
  if (outcome.kind === "awaiting") {
    return (
      <ShieldQuestion
        size={14}
        strokeWidth={2}
        className="shrink-0 text-[var(--color-brand)]"
        aria-hidden
      />
    );
  }
  if (outcome.kind === "pending") {
    return (
      <Loader2
        size={14}
        strokeWidth={2}
        className="shrink-0 animate-spin text-[var(--color-text-muted)]"
        aria-hidden
      />
    );
  }
  if (outcome.kind === "unknown") {
    return (
      <CircleDashed
        size={14}
        strokeWidth={2}
        className="shrink-0 text-[var(--color-text-muted)]"
        aria-hidden
      />
    );
  }
  switch (outcome.word) {
    case "success":
      return <Check size={14} strokeWidth={2} className="shrink-0 text-emerald-600" aria-hidden />;
    case "failed":
      return <X size={14} strokeWidth={2} className="shrink-0 text-red-600" aria-hidden />;
    case "denied":
      return (
        <ShieldAlert size={14} strokeWidth={2} className="shrink-0 text-amber-600" aria-hidden />
      );
    default:
      return (
        <Check
          size={14}
          strokeWidth={2}
          className="shrink-0 text-[var(--color-text-muted)]"
          aria-hidden
        />
      );
  }
}

function OutcomeBadge({ outcome }: { outcome: ToolOutcome }) {
  const { t } = useTranslation();
  const NEUTRAL = "bg-[var(--color-hover-faint)] text-[var(--color-text-muted)]";

  const { label, classes } = (() => {
    if (outcome.kind === "awaiting") {
      return {
        label: t("intelligence.toolStrip.outcome.awaiting"),
        classes:
          "bg-[color-mix(in_srgb,var(--color-brand)_12%,transparent)] text-[var(--color-brand)]",
      };
    }
    if (outcome.kind === "pending") {
      return { label: t("intelligence.toolStrip.outcome.pending"), classes: NEUTRAL };
    }
    if (outcome.kind === "unknown") {
      return { label: t("intelligence.toolStrip.outcome.unknown"), classes: NEUTRAL };
    }
    switch (outcome.word) {
      case "success":
        return {
          label: t("intelligence.toolStrip.outcome.success"),
          classes: "bg-emerald-50 text-emerald-700",
        };
      case "failed":
        return {
          label: t("intelligence.toolStrip.outcome.failed"),
          classes: "bg-red-50 text-red-700",
        };
      case "denied":
        return {
          label: t("intelligence.toolStrip.outcome.denied"),
          classes: "bg-amber-50 text-amber-700",
        };
      default:
        return { label: outcome.word, classes: NEUTRAL };
    }
  })();

  return (
    <span
      className={cn(
        "rounded-full px-[8px] py-[2px] text-xs font-medium uppercase tracking-[0.04em]",
        classes,
      )}
      data-outcome={outcomeKey(outcome)}
    >
      {label}
    </span>
  );
}

function toolLabel(t: TranslateFn, name: string): string {
  return name
    ? t(`intelligence.toolStrip.names.${name}`, { defaultValue: humanizeToolName(name) })
    : t("intelligence.toolStrip.unknownTool");
}

function humanizeToolName(name: string): string {
  const spaced = name
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/_+/g, " ")
    .trim()
    .toLowerCase();
  if (!spaced) return name;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const MIN_PENDING_MS = 350;

const RUN_OUTCOME_ORDER: readonly string[] = [
  "awaiting",
  "pending",
  "word:failed",
  "word:denied",
  "unknown",
];

const STAGE_OUTCOME: Record<ToolStage, ToolOutcome> = {
  awaiting: OUTCOME_AWAITING,
  done: { kind: "word", word: "success" },
};
